/**
 * use-condition-rules — dane reguł UPRAWNIEŃ WARUNKOWYCH (F24).
 *
 * Reguła warunkowa jest bytem nazwanym i przypinanym do wielu podmiotów, więc ma własny zestaw
 * zapytań — inaczej niż siatka uprawnień, gdzie wszystko kręci się wokół JEDNEGO wybranego
 * podmiotu.
 *
 * Lista dopuszczonych pól przychodzi z serwera i panel jej nie uzupełnia. To celowe: gdyby ekran
 * miał własny wykaz, prędzej czy później zaproponowałby pole, którego zapis nie przyjmie.
 */

import type { PermissionAction, PermissionCondition } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type ConditionObjectKind = 'kartoteka' | 'doc_class' | 'doc_type' | 'category';

export interface ConditionRuleSubject {
  kind: 'role' | 'team' | 'user';
  id: string;
}

export interface ConditionRule {
  id: string;
  name: string;
  description: string | null;
  kartotekaKey: string;
  objectKind: ConditionObjectKind;
  objectKey: string | null;
  objectId: string | null;
  actions: PermissionAction[];
  condition: PermissionCondition;
  isEnabled: boolean;
  /** Wypełnione, gdy reguła przestała być wykonalna — ekran oznacza ją jako zepsutą. */
  brokenReason: string | null;
  subjects: ConditionRuleSubject[];
}

export interface ConditionFieldOption {
  path: string;
  label: string;
  kind: 'text' | 'number' | 'date' | 'timestamp' | 'boolean' | 'uuid' | 'text_array';
  /** Obecne, gdy pole trzyma identyfikator osoby albo jednostki — wtedy wolno wstawić `@userId`. */
  subject?: 'user' | 'employee' | 'org_unit';
}

export interface ConditionFieldGroup {
  group: string;
  label: string;
  fields: ConditionFieldOption[];
}

/** Treść reguły wysyłana przy zakładaniu i zmianie — po obu stronach ten sam kształt. */
export interface ConditionRuleInput {
  companyId: string;
  name: string;
  description?: string | null;
  kartotekaKey: string;
  objectKind: ConditionObjectKind;
  objectKey?: string | null;
  objectId?: string | null;
  actions: PermissionAction[];
  condition: PermissionCondition;
  isEnabled: boolean;
  subjects: ConditionRuleSubject[];
}

export const CONDITION_RULES_KEY = (companyId: string) => ['permission-condition-rules', companyId];
export const CONDITION_FIELDS_KEY = (companyId: string, kartoteka: string) => [
  'permission-condition-fields',
  companyId,
  kartoteka,
];

export function useConditionRules(companyId: string | null) {
  return useQuery<{ companyId: string; rules: ConditionRule[] }>({
    queryKey: CONDITION_RULES_KEY(companyId ?? 'none'),
    queryFn: async () => {
      const res = await api.get<{ companyId: string; rules: ConditionRule[] }>(
        `/permissions/condition-rules?companyId=${companyId}`,
      );
      return res.data;
    },
    enabled: Boolean(companyId),
    staleTime: 10_000,
  });
}

/** Pola dopuszczone w warunku dla wybranej kartoteki. */
export function useConditionFields(
  companyId: string | null,
  kartoteka: string | null,
  /** Klasa dokumentu — odblokowuje pola dodatkowe i tabele dodatkowe tej klasy. */
  docClass: string | null = null,
) {
  return useQuery<{ kartoteka: string; groups: ConditionFieldGroup[] }>({
    queryKey: [...CONDITION_FIELDS_KEY(companyId ?? 'none', kartoteka ?? 'none'), docClass ?? ''],
    queryFn: async () => {
      const classParam = docClass ? `&docClass=${encodeURIComponent(docClass)}` : '';
      const res = await api.get<{ kartoteka: string; groups: ConditionFieldGroup[] }>(
        `/permissions/condition-fields?companyId=${companyId}&kartoteka=${encodeURIComponent(kartoteka!)}${classParam}`,
      );
      return res.data;
    },
    enabled: Boolean(companyId && kartoteka),
    // Zestaw pól zmienia się tylko przy zmianie definicji kartoteki — trzymamy dłużej.
    staleTime: 300_000,
  });
}

export function useSaveConditionRule(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation<{ id: string }, unknown, { ruleId: string | null; input: ConditionRuleInput }>(
    {
      mutationFn: async ({ ruleId, input }) => {
        const res = ruleId
          ? await api.patch<{ id: string }>(`/permissions/condition-rules/${ruleId}`, input)
          : await api.post<{ id: string }>('/permissions/condition-rules', input);
        return res.data;
      },
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: CONDITION_RULES_KEY(companyId ?? 'none') });
      },
    },
  );
}

export function useDeleteConditionRule(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (ruleId) => {
      await api.delete(`/permissions/condition-rules/${ruleId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CONDITION_RULES_KEY(companyId ?? 'none') });
    },
  });
}
